import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, Spinner } from "../components/ui-kit";
import AIResearch from "./AIResearch";
import { SignalFeed } from "../components/hub/SignalFeed";
import { ChartFeed } from "../components/hub/ChartFeed";
import { EventsFeed } from "../components/hub/EventsFeed";
import { Blog } from "../components/hub/Blog";
import { AIChat } from "../components/hub/AIChat";
import { PushToggle } from "../components/PushToggle";
import { MenuTiles, VIP_TILE, SeeMore } from "../components/hub/MenuTiles";
import { MobileTabBar } from "../components/MobileTabBar";
import { LogOut, LayoutDashboard, Radio, Bot, Newspaper, Lock, LineChart, BookOpen, MessageCircle, ArrowLeft } from "lucide-react";

const TABS = [
  { k: "home", label: "Dashboard", short: "Home", icon: LayoutDashboard, perm: null },
  { k: "signals", label: "Signals", icon: Radio, perm: null, d: "Sabhi Gold & Bitcoin signals" },
  { k: "charts", label: "Chart Analysis", short: "Charts", icon: LineChart, perm: null, d: "AI chart breakdown" },
  { k: "news", label: "News & Events", icon: Newspaper, perm: null, d: "High-impact news alerts" },
  { k: "blog", label: "Blog", icon: BookOpen, perm: null, d: "Analysis & education" },
  { k: "chat", label: "Fateh AI Chat", icon: MessageCircle, perm: null, d: "Kuch bhi puchiye" },
  { k: "ai", label: "AI Research", icon: Bot, perm: "gold_ai", d: "Deep market research" },
];

function Home({ data, tabs, go }) {
  return (
    <div className="space-y-5">
      <Panel title="Signals" testid="client-signals-preview" right={<SeeMore onClick={() => go("signals")} testid="client-signals-see-more" />}>
        <div className="p-3"><SignalFeed compact limit={2} /></div>
      </Panel>
      <MenuTiles items={[...tabs.filter((t) => t.k !== "home").map((t) => ({ ...t, l: t.label })), VIP_TILE]} onPick={go} />

      {data.news && (
        <Panel title="Important News">
          {data.news.map((n) => (
            <div key={n.id} className="px-4 py-2.5 border-b border-[color:var(--border-subtle)] last:border-0 text-sm text-slate-300">{n.title} <span className={`text-xs ml-1 ${n.affects === "Gold" ? "text-gold" : "text-btc"}`}>{n.affects}</span></div>
          ))}
          {!data.news.length && <div className="px-4 py-4 text-sm text-slate-500" data-testid="client-news-empty">Abhi koi important news nahi. News & Events tab me upcoming events dekhein.</div>}
        </Panel>
      )}
    </div>
  );
}
function Locked({ label }) {
  return (
    <div className="surface p-10 text-center">
      <Lock size={28} className="text-slate-600 mx-auto mb-3" />
      <div className="text-white font-head">{label} not included in your plan</div>
      <p className="text-sm text-slate-500 mt-1">Contact support to upgrade your research access.</p>
    </div>
  );
}

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("home");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get("/portal/summary").then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || "Access error"));
  }, []);

  const has = (p) => !p || (data?.permissions || []).includes(p);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center grain p-6" style={{ background: "var(--app-bg)" }}>
      <div className="surface p-8 text-center max-w-sm">
        <Lock size={30} className="text-gold mx-auto mb-3" />
        <div className="text-white font-head text-lg">{err}</div>
        <button onClick={logout} className="btn-gold px-4 py-2 text-sm mt-5">Back to Login</button>
      </div>
    </div>
  );
  const tabs = TABS.filter((t) => t.k !== "ai" || has("gold_ai") || has("btc_ai"));
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--app-bg)" }}><Spinner /></div>;

  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }}>
      <header className="h-16 sticky top-0 z-20 glass flex items-center px-4 md:px-6 gap-3">
        {tab !== "home" && <button onClick={() => setTab("home")} data-testid="client-back-home" className="text-slate-300 hover:text-gold transition-colors duration-200 -ml-1 p-1"><ArrowLeft size={20} /></button>}
        <div className="flex items-center gap-2"><div className="w-8 h-8 bg-gold text-black flex items-center justify-center font-head font-bold">F</div><span className="font-head font-bold text-white">Fateh FX</span><span className="text-[10px] text-slate-500 uppercase tracking-widest hidden sm:block">Client Portal</span></div>
        <div className="ml-auto flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 border ${data.premium ? "border-gold text-gold" : "border-slate-600 text-slate-400"}`}>{data.plan}</span>
          <span className="text-sm text-white hidden sm:block">{user?.name}</span>
          <PushToggle compact />
          <button onClick={logout} data-testid="client-logout" className="text-slate-400 hover:text-bear transition-colors duration-200"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="p-4 pb-24 md:p-6 max-w-screen-xl mx-auto">
        {tab !== "home" && <h1 className="font-head text-2xl text-white mb-4 tracking-tight" data-testid="client-tab-title">{tabs.find((t) => t.k === tab)?.label}</h1>}
        {tab === "home" && <Home data={data} tabs={tabs} go={setTab} />}
        {tab === "signals" && <SignalFeed />}
        {tab === "charts" && <ChartFeed />}
        {tab === "news" && <EventsFeed />}
        {tab === "blog" && <Blog />}
        {tab === "chat" && <AIChat />}
        {tab === "ai" && (has("gold_ai") || has("btc_ai") ? <AIResearch embed /> : <Locked label="AI Research" />)}
      </main>
      <MobileTabBar tabs={tabs} primary={["home", "signals", "charts", "blog"]} active={tab} onPick={(k) => { setTab(k); window.scrollTo(0, 0); }} extra={[VIP_TILE]} />
    </div>
  );
}
